import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import {getApplicantInformation} from 'src/action-creators/crm';
import {
  selectApplicantInformation,
  selectHasCrmPermission,
  selectUserInfo,
} from 'src/selectors/crm';
import {useSelector, useDispatch} from 'react-redux';

import {CRM_PERMISSIONS} from 'src/utils/crm-new/common';

import CandidateResponseForm from 'src/components/crm/applicants/body/tabComponents/forms/candidate-response-form';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';
import InternalResponseForm from 'src/components/crm/applicants/body/tabComponents/forms/internal-response-form';
import {
  Card,
  CardTitle,
  CardHeader,
  CardContent,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {BodyMedium} from '@spaced-out/ui-design-system/lib/components/Text';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';

import {useInflight} from 'src/hooks/redux';

import css from './candidate-forms-submitted.css';


const CandidateFormsSubmitted = ({
  jobId,
  candidateId,
  isPublicLink,
  reloadCandidateData,
  showActionButtons = true,
}: {
  jobId: number;
  candidateId: number;
  isPublicLink?: boolean;
  reloadCandidateData: () => void;
  showActionButtons?: boolean;
}) => {
  const dispatch = useDispatch();
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const userInfo = useSelector(selectUserInfo);
  const viewCandidateForm = selectHasCrmPermission(
    userInfo,
    CRM_PERMISSIONS.VIEW_CANDIDATE_FORM,
  );
  const applicantDetails = useSelector(selectApplicantInformation);
  const isApplicantsLoading = useInflight(
    getApplicantInformation(candidateId, jobId.toString()),
  );

  const currentJobApplication = applicantDetails?.applications?.find(
    (application: any) =>
      application.jobId === jobId &&
      !application.duplicate &&
      application.jobId > 0 &&
      application.active,
  );

  React.useEffect(() => {
    if (!applicantDetails && Boolean(candidateId) && Boolean(jobId)) {
      dispatch(getApplicantInformation(candidateId, jobId.toString()));
    }
  }, []);

  if (isApplicantsLoading) {
    return <CircularLoader />;
  }

  if (!currentJobApplication) {
    return null;
  }

  const {stepCandidates} = currentJobApplication;

  const formInterviews: Array<{
    submittedForm: any;
    type: string;
  }> = [];
  stepCandidates.forEach((sc) => {
    // @ts-expect-error - TS2339 - Property 'interviewCandidateFormField' does not exist on type 'ApplicationStepCandidate'.
    if (sc.interviewCandidateFormField) {
      formInterviews.push({
        type: 'CANDIDATE_FORM',
        submittedForm: sc,
      });
      // @ts-expect-error - TS2339 - Property 'jobInternalForm' does not exist on type 'ApplicationStepCandidate'.
    } else if (sc.jobInternalForm) {
      formInterviews.push({
        type: 'INTERNAL_FORM',
        submittedForm: sc,
      });
    }
  });

  return (
    <Card classNames={{wrapper: css.formsCard}}>
      <CardHeader>
        <CardTitle>{t('Forms', 'Forms')}</CardTitle>
      </CardHeader>
      <CardContent className={css.formsCardContentContainer}>
        {formInterviews.length > 0 ? (
          // @ts-expect-error - TS7030 - Not all code paths return a value.
          formInterviews.map((formInterview) => {
            if (formInterview.type === 'CANDIDATE_FORM' && viewCandidateForm) {
              return (
                <>
                  <CandidateResponseForm
                    formInterview={formInterview.submittedForm}
                    applicationId={currentJobApplication.id}
                    isPublicLink={isPublicLink}
                    candidateName={applicantDetails.name ?? ''}
                    reloadCandidateData={reloadCandidateData}
                    showActionButtons={showActionButtons}
                  />
                </>
              );
            }
            if (formInterview.type === 'INTERNAL_FORM') {
              return (
                <>
                  <InternalResponseForm
                    formInterview={formInterview.submittedForm}
                    applicationId={currentJobApplication.id}
                    isPublicLink={isPublicLink}
                    candidateName={applicantDetails.name ?? ''}
                  />
                </>
              );
            }
          })
        ) : (
          <div className={css.emptyStateContainer}>
            <EmptyState
              title={t('No_Submissions_Yet', 'No Submissions Yet')}
              description={t(
                'No_forms_have_been_submitted_yet',
                'No forms have been submitted yet',
              )}
              imageVariant="data"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CandidateFormsSubmitted;
